import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Check, Clock, FileText, Phone, Mail, Shield, X } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_INFO = {
  draft: { label: 'Pending', icon: Clock, color: 'text-gray-600' },
  sent: { label: 'Awaiting Response', icon: Clock, color: 'text-blue-600' },
  viewed: { label: 'Awaiting Response', icon: Clock, color: 'text-yellow-600' },
  accepted: { label: 'Accepted', icon: Check, color: 'text-green-600' },
  declined: { label: 'Declined', icon: X, color: 'text-red-600' },
};

export default function QuoteView() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/quotes/${token}`)
      .then(r => { if (!r.ok) throw new Error('Quote not found'); return r.json(); })
      .then(data => setQuote(data))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/quotes/${token}/accept`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) setQuote(q => ({ ...q, status: 'accepted' }));
      else alert(data.detail || 'Could not accept');
    } catch { alert('Network error'); }
    setAccepting(false);
  };

  const handleDecline = async () => {
    if (!window.confirm('Are you sure you want to decline this proposal?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/quotes/${token}/decline`, { method: 'POST' });
      if (res.ok) setQuote(q => ({ ...q, status: 'declined' }));
    } catch { alert('Network error'); }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center"><FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" /><p className="text-gray-500 text-lg">{error}</p></div>
    </div>
  );

  const st = STATUS_INFO[quote.status] || STATUS_INFO.sent;
  const StIcon = st.icon;
  const canAct = ['sent', 'viewed'].includes(quote.status);
  const validDate = quote.validUntil ? new Date(quote.validUntil).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4" data-testid="quote-view-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-700 to-teal-700 rounded-t-2xl p-8 text-white">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold mb-1">Virtual CFO Consultancy</h1>
              <p className="text-emerald-200 text-sm">Consultants India Private Limited</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-emerald-200">Proposal</p>
              <p className="text-lg font-bold" data-testid="quote-number">{quote.quoteNumber}</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="bg-white rounded-b-2xl shadow-lg">
          {/* Status Bar */}
          <div className={`px-8 py-3 border-b flex items-center gap-2 ${st.color}`}>
            <StIcon className="w-4 h-4" />
            <span className="font-medium text-sm">Status: {st.label}</span>
            {quote.status === 'accepted' && <span className="ml-auto text-xs text-gray-400">Accepted on {new Date(quote.acceptedAt).toLocaleDateString('en-IN')}</span>}
          </div>

          <div className="p-8">
            {/* Client Info */}
            <div className="mb-8">
              <p className="text-sm text-gray-500 mb-1">Prepared for</p>
              <p className="text-xl font-bold text-gray-900" data-testid="quote-client-name">{quote.leadName}</p>
              {quote.leadCompany && <p className="text-gray-600">{quote.leadCompany}</p>}
              <p className="text-sm text-gray-500">{quote.leadEmail}</p>
            </div>

            {/* Services Table */}
            <table className="w-full mb-6" data-testid="quote-items-table">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase pb-3">Service</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase pb-3">Description</th>
                  <th className="text-right text-xs font-semibold text-gray-500 uppercase pb-3">Monthly</th>
                </tr>
              </thead>
              <tbody>
                {(quote.items || []).map((item, i) => (
                  <tr key={`quote-item-${i}`} className="border-b border-gray-100" data-testid={`quote-item-${i}`}>
                    <td className="py-3 font-medium text-gray-900 text-sm">{item.name}</td>
                    <td className="py-3 text-gray-500 text-sm">{item.description}</td>
                    <td className="py-3 text-right font-semibold text-sm">₹{item.amount?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div className="bg-gray-50 rounded-xl p-5 space-y-2 mb-8">
              <div className="flex justify-between text-sm"><span className="text-gray-600">Subtotal</span><span className="font-medium">₹{quote.subtotal?.toLocaleString()}/mo</span></div>
              {quote.discount > 0 && (
                <div className="flex justify-between text-sm text-emerald-600"><span>Discount ({quote.discountPercent}%)</span><span>-₹{quote.discount?.toLocaleString()}</span></div>
              )}
              <div className="flex justify-between text-sm"><span className="text-gray-600">GST ({quote.taxRate}%)</span><span>₹{quote.tax?.toLocaleString()}</span></div>
              <div className="flex justify-between pt-3 border-t-2 border-emerald-500 text-lg">
                <span className="font-bold text-gray-900">Total</span>
                <span className="font-bold text-emerald-600" data-testid="quote-total">₹{quote.total?.toLocaleString()}/mo</span>
              </div>
            </div>

            {/* Notes */}
            {quote.notes && (
              <div className="mb-8 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">{quote.notes}</p>
              </div>
            )}

            {/* Valid Until */}
            {validDate && (
              <p className="text-sm text-gray-500 mb-6 flex items-center gap-2"><Clock className="w-4 h-4" /> This proposal is valid until <strong>{validDate}</strong></p>
            )}

            {/* Action Buttons */}
            {canAct && (
              <div className="flex gap-3" data-testid="quote-actions">
                <Button onClick={handleAccept} disabled={accepting} className="flex-1 bg-emerald-600 hover:bg-emerald-700 py-6 text-base" data-testid="accept-quote-btn">
                  <Check className="w-5 h-5 mr-2" /> {accepting ? 'Processing...' : 'Accept Proposal'}
                </Button>
                <Button onClick={handleDecline} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 py-6" data-testid="decline-quote-btn">
                  <X className="w-5 h-5 mr-2" /> Decline
                </Button>
              </div>
            )}

            {quote.status === 'accepted' && (
              <div className="bg-emerald-50 rounded-xl p-6 text-center border border-emerald-200" data-testid="quote-accepted-banner">
                <Check className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-emerald-800 mb-1">Proposal Accepted!</h3>
                <p className="text-emerald-700">Our team will reach out within 24 hours to begin onboarding.</p>
              </div>
            )}

            {quote.status === 'declined' && (
              <div className="bg-red-50 rounded-xl p-6 text-center border border-red-200">
                <X className="w-12 h-12 text-red-400 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-red-800 mb-1">Proposal Declined</h3>
                <p className="text-red-600">Changed your mind? Contact us anytime.</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 rounded-b-2xl px-8 py-5 border-t flex items-center justify-between text-sm text-gray-500">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> +91 7977789741</span>
              <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> info@nsrm.in</span>
            </div>
            <span className="flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> 256-bit encrypted</span>
          </div>
        </div>
      </div>
    </div>
  );
}
